import React, { useState, useEffect } from 'react';
import { authFetch } from '../services/api';
import './IntegrationApplicationMasterData.css';

interface Application {
  application_id_code: string;
  application_name: string;
  technology_stack?: string;
  it_hardware_infrastructure?: string;
  recommendation: 'Retire' | 'Re-Platform' | 'Re-Design' | 'Retain';
  application_dependencies?: string;
  integration_dependencies?: string;
}

interface IntegrationGroup {
  group_id: string;
  group_name: string;
  group_description?: string;
  applications?: string[]; // Array of application IDs (parsed from application_list)
  application_list?: string; // Database field - semicolon separated
  execution_order?: string; // Database field - comma separated
  execution_sequence?: string[]; // Parsed from execution_order
  earliest_start_date?: string;
  group_complexity?: 'High' | 'Medium' | 'Low';
  roadmap_grouping_status?: 'Single Disposition' | 'Integration Required';
  application_dependencies_exist?: 'YES' | 'NO';
  integration_dependencies_exist?: 'YES' | 'NO';
  created_date?: string;
  created_by?: string;
  // Additional UI fields for compatibility
  groupComposition?: 'retirement-only' | 'migration-only' | 'mixed';
  dependencyPattern?: 'standalone' | 'internal-dependencies' | 'external-dependencies' | 'foundation-apps';
  timingScenario?: 'immediate-start' | 'sequential-required' | 'parallel-possible' | 'external-timing';
  resourceRequirements?: 'specialized-skills' | 'generic-skills' | 'dedicated-teams' | 'shared-resources';
  businessComplexity?: 'simple' | 'complex' | 'regulatory';
  specializedSkills?: string[];
  estimatedDuration?: number;
  priority?: 'high' | 'medium' | 'low';
  exclude_from_analysis?: boolean;
  license_renegotiation?: boolean;
  consolidation_group?: boolean;
  cloud_migration?: boolean;
}

interface ScheduleRecord {
  application_id: string;
  integration_group_id: string;
  schedule_status: string;
  deployment_status?: string;
  planned_end_date?: string;
  phase_golive_end?: string;
}

const IntegrationGroupings: React.FC = () => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [integrationGroups, setIntegrationGroups] = useState<IntegrationGroup[]>([]);
  const [selectedApplications, setSelectedApplications] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<IntegrationGroup | null>(null);
  const [showAddAppsModal, setShowAddAppsModal] = useState(false);
  const [targetGroup, setTargetGroup] = useState<IntegrationGroup | null>(null);
  const [appsToAdd, setAppsToAdd] = useState<string[]>([]);
  const [removingApplications, setRemovingApplications] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [groupSearchTerm, setGroupSearchTerm] = useState('');
  const [filterRecommendation, setFilterRecommendation] = useState('all');
  const [activeTab, setActiveTab] = useState<'selection' | 'groups'>('selection');
  const [showExistingRelationshipsWarning, setShowExistingRelationshipsWarning] = useState(false);
  const [existingRelationships, setExistingRelationships] = useState<{ appId: string, appName: string, groups: { groupId: string, groupName: string }[] }[]>([]);
  const [groupHasDependencies, setGroupHasDependencies] = useState(false);
  const [groupHasScheduleRecords, setGroupHasScheduleRecords] = useState(false);
  
  // Form state for new/edit group
  const [groupForm, setGroupForm] = useState<Partial<IntegrationGroup>>({
    group_name: '',
    group_description: '',
    applications: [],
    application_dependencies_exist: 'YES',
    earliest_start_date: '',
    created_by: 'Current User'
  });

  // Restricted applications state
  const [restrictedApplications, setRestrictedApplications] = useState<string[]>([]);
  const [scheduleData, setScheduleData] = useState<ScheduleRecord[]>([]);

  useEffect(() => {
    loadApplications();
    loadIntegrationGroups();
    loadRestrictedApplications();
    loadScheduleData();
  }, []);

  const loadRestrictedApplications = async () => {
    try {
      const response = await authFetch('/api/integration-groups/restricted-applications');
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          const restrictedIds = result.data.map((item: any) => item.application_id);
          setRestrictedApplications(restrictedIds);
        }
      }
    } catch (err) {
      console.error('Error loading restricted applications:', err);
    }
  };

  const loadScheduleData = async () => {
    try {
      const response = await authFetch('/api/roadmap-schedule');
      const result = await response.json();
      if (result.success && Array.isArray(result.data)) {
        setScheduleData(result.data);
      } else {
        setScheduleData([]);
      }
    } catch (err: any) {
      console.error('Error loading schedule data:', err);
      setScheduleData([]);
    }
  };

  const loadApplications = async () => {
    try {
      setLoading(true);
      const response = await authFetch('/api/applications-master?limit=500');
      if (!response.ok) throw new Error('Failed to fetch applications');

      const data = await response.json();
      if (data.success) {
        setApplications(data.data);
      }
    } catch (err: any) {
      console.error('Error loading applications:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadIntegrationGroups = async () => {
    try {
      const response = await authFetch('/api/integration-groups');
      if (!response.ok) throw new Error('Failed to fetch integration groups');
      
      const data = await response.json();
      if (data.success) {
        setIntegrationGroups(data.data || []);
      }
    } catch (err: any) {
      console.error('Error loading integration groups:', err);
      setError(`Failed to load integration groups: ${err.message}`);
      setIntegrationGroups([]);
    }
  };

  // Get all applications that are already assigned to integration groups
  const getAssignedApplications = (): string[] => {
    const assigned: string[] = [];
    integrationGroups.forEach(group => {
      if (group.applications) {
        assigned.push(...group.applications);
      }
    });
    return assigned;
  };

  // Check if an application is already assigned to a group (excluding the group being edited)
  // Retire and Re-Platform: Can only belong to ONE group (single assignment)
  // Retain and Re-Design: Can belong to multiple groups
  const isApplicationAssigned = (appId: string): boolean => {
    // Find the application to check its recommendation
    const app = applications.find(a => a.application_id_code === appId);

    // If it's a Retain or Re-Design application, it can be selected for multiple groups
    if (app && (app.recommendation === 'Retain' || app.recommendation === 'Re-Design')) {
      return false; // Never consider Retain/Re-Design apps as "assigned" (always selectable)
    }

    // For Retire and Re-Platform applications, use the original logic (single group only)
    const assigned: string[] = [];
    integrationGroups.forEach(group => {
      // Skip the group being edited (if any)
      if (editingGroup && group.group_id === editingGroup.group_id) {
        return;
      }
      if (group.applications) {
        assigned.push(...group.applications);
      }
    });
    return assigned.includes(appId);
  };

  // Find which group an application is assigned to
  const getAssignedGroup = (appId: string): IntegrationGroup | null => {
    for (const group of integrationGroups) {
      // Skip the group being edited (if any)
      if (editingGroup && group.group_id === editingGroup.group_id) {
        continue;
      }
      if (group.applications && group.applications.includes(appId)) {
        return group;
      }
    }
    return null;
  };

  // Get the deployment end date (GoLive end date) for an application
  const getApplicationDeploymentEndDate = (appId: string): string | null => {
    const schedule = scheduleData.find(s => s.application_id === appId);
    if (schedule && schedule.phase_golive_end) {
      return schedule.phase_golive_end.split('T')[0]; // Return date part only
    }
    return null;
  };

  // Get the minimum allowed start date for a set of applications
  // For Retain/Re-Design apps already in another group, the start date must be after their deployment end date
  const getMinimumStartDateForApplications = (appIds: string[]): string | null => {
    let maxDeploymentEndDate: string | null = null;

    appIds.forEach(appId => {
      const app = applications.find(a => a.application_id_code === appId);
      // Only check Retain and Re-Design apps that are already assigned to another group
      if (app && (app.recommendation === 'Retain' || app.recommendation === 'Re-Design')) {
        const assignedGroup = getAssignedGroup(appId);
        if (assignedGroup) {
          const deploymentEndDate = getApplicationDeploymentEndDate(appId);
          if (deploymentEndDate) {
            if (!maxDeploymentEndDate || deploymentEndDate > maxDeploymentEndDate) {
              maxDeploymentEndDate = deploymentEndDate;
            }
          }
        }
      }
    });

    return maxDeploymentEndDate;
  };

  // Get applications that have start date constraints (Retain/Re-Design in other groups)
  const getAppsWithStartDateConstraints = (appIds: string[]): { appId: string; appName: string; deploymentEndDate: string; groupName: string }[] => {
    const constraints: { appId: string; appName: string; deploymentEndDate: string; groupName: string }[] = [];

    appIds.forEach(appId => {
      const app = applications.find(a => a.application_id_code === appId);
      if (app && (app.recommendation === 'Retain' || app.recommendation === 'Re-Design')) {
        const assignedGroup = getAssignedGroup(appId);
        if (assignedGroup) {
          const deploymentEndDate = getApplicationDeploymentEndDate(appId);
          if (deploymentEndDate) {
            constraints.push({
              appId,
              appName: app.application_name,
              deploymentEndDate,
              groupName: assignedGroup.group_name
            });
          }
        }
      }
    });

    return constraints;
  };

  const handleApplicationToggle = (appId: string) => {
    // Find the application to check its recommendation
    const app = applications.find(a => a.application_id_code === appId);

    // For RETIRE and RE-PLATFORM applications, prevent selection if already assigned (single group only)
    if (app && (app.recommendation === 'Retire' || app.recommendation === 'Re-Platform') && isApplicationAssigned(appId)) {
      return; // Do nothing if RETIRE/RE-PLATFORM app is already assigned
    }

    // For RETAIN and RE-DESIGN applications, always allow selection (multiple groups allowed)
    setSelectedApplications(prev =>
      prev.includes(appId)
        ? prev.filter(id => id !== appId)
        : [...prev, appId]
    );
  };

  const handleSelectAll = () => {
    const filteredApps = getFilteredApplications();
    // Only select applications that are not already assigned to groups
    const availableAppIds = filteredApps
      .filter(app => !isApplicationAssigned(app.application_id_code))
      .map(app => app.application_id_code);
    setSelectedApplications(availableAppIds);
  };

  const handleClearSelection = () => {
    setSelectedApplications([]);
  };

  const getFilteredApplications = () => {
    const filtered = applications.filter(app => {
      const matchesSearch = searchTerm === '' || 
        app.application_id_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.application_name.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesFilter = filterRecommendation === 'all' || 
        app.recommendation === filterRecommendation;
      
      return matchesSearch && matchesFilter;
    });

    // Sort applications: unassigned first, assigned last
    return filtered.sort((a, b) => {
      const aAssigned = isApplicationAssigned(a.application_id_code);
      const bAssigned = isApplicationAssigned(b.application_id_code);
      
      // If assignment status is different, sort by assignment (unassigned first)
      if (aAssigned !== bAssigned) {
        return aAssigned ? 1 : -1; // unassigned (-1) comes before assigned (1)
      }
      
      // If same assignment status, maintain alphabetical order by application ID
      return a.application_id_code.localeCompare(b.application_id_code);
    });
  };

  const analyzeGroupComposition = (appIds: string[]): 'retirement-only' | 'migration-only' | 'mixed' => {
    const selectedApps = applications.filter(app => appIds.includes(app.application_id_code));
    const hasRetirement = selectedApps.some(app => app.recommendation === 'Retire');
    const hasMigration = selectedApps.some(app => 
      app.recommendation === 'Re-Platform' || app.recommendation === 'Re-Design'
    );
    
    if (hasRetirement && !hasMigration) return 'retirement-only';
    if (hasMigration && !hasRetirement) return 'migration-only';
    return 'mixed';
  };

  const checkExistingRelationships = (appIds: string[]) => {
    const relationships: { appId: string, appName: string, groups: { groupId: string, groupName: string }[] }[] = [];
    
    appIds.forEach(appId => {
      const app = applications.find(a => a.application_id_code === appId);
      const assignedGroups: { groupId: string, groupName: string }[] = [];
      
      integrationGroups.forEach(group => {
        if (group.applications && group.applications.includes(appId)) {
          assignedGroups.push({
            groupId: group.group_id,
            groupName: group.group_name
          });
        }
      });
      
      if (assignedGroups.length > 0) {
        relationships.push({
          appId: appId,
          appName: app?.application_name || appId,
          groups: assignedGroups
        });
      }
    });
    
    return relationships;
  };

  const handleCreateGroup = () => {
    // Check for existing relationships
    const relationships = checkExistingRelationships(selectedApplications);
    console.log('🔍 Debug - Selected applications:', selectedApplications);
    console.log('🔍 Debug - Found relationships:', relationships);
    console.log('🔍 Debug - Integration groups:', integrationGroups);
    
    if (relationships.length > 0) {
      // Show warning modal with existing relationships
      setExistingRelationships(relationships);
      setShowExistingRelationshipsWarning(true);
      console.log('🔍 Debug - Showing warning modal with relationships:', relationships);
    } else {
      // No existing relationships, proceed normally
      proceedWithGroupCreation();
    }
  };

  const proceedWithGroupCreation = () => {
    setGroupForm({
      group_name: '',
      group_description: '',
      applications: selectedApplications,
      application_dependencies_exist: 'YES',
      earliest_start_date: '',
      created_by: 'Current User'
    });
    setGroupHasDependencies(false); // New group has no dependencies
    setGroupHasScheduleRecords(false); // New group has no schedule records
    setShowCreateGroupModal(true);
  };

  const handleSaveGroup = async () => {
    try {
      if (!groupForm.group_name?.trim()) {
        alert('Group name is required');
        return;
      }

      // Validate start date constraint for Retain/Re-Design apps
      const minStartDate = getMinimumStartDateForApplications(groupForm.applications || []);
      if (minStartDate && groupForm.earliest_start_date) {
        if (groupForm.earliest_start_date < minStartDate) {
          const appsWithConstraints = getAppsWithStartDateConstraints(groupForm.applications || []);
          const constraintDetails = appsWithConstraints.map(c =>
            `${c.appId} completes deployment in "${c.groupName}" on ${c.deploymentEndDate}`
          ).join('\n');
          alert(`Start date cannot be before ${minStartDate}.\n\nThe following applications have deployment constraints:\n${constraintDetails}`);
          return;
        }
      }

      let payload: any = {
        group_name: groupForm.group_name,
        group_description: groupForm.group_description || '',
        applications: groupForm.applications || [],
        execution_sequence: groupForm.applications || [],
        earliest_start_date: groupForm.earliest_start_date || null,
        application_dependencies_exist: groupForm.application_dependencies_exist || 'YES',
        created_by: groupForm.created_by || 'Current User',
        exclude_from_analysis: groupForm.exclude_from_analysis || false,
        license_renegotiation: groupForm.license_renegotiation || false,
        consolidation_group: groupForm.consolidation_group || false,
        cloud_migration: groupForm.cloud_migration || false
      };

      if (editingGroup) {
        // Update existing group (roadmap_grouping_status is excluded from payload)
        const response = await authFetch(`/api/integration-groups/${editingGroup.group_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to update group');
        }
      } else {
        // Create new group
        const response = await authFetch('/api/integration-groups', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create group');
        }
      }

      // Reload groups from database
      await loadIntegrationGroups();
      await loadRestrictedApplications(); // Reload restricted apps after group changes

      // Reset form
      setShowCreateGroupModal(false);
      setEditingGroup(null);
      setSelectedApplications([]);
      setGroupHasDependencies(false);
      setGroupHasScheduleRecords(false);
      setGroupForm({
        group_name: '',
        group_description: '',
        applications: [],
        application_dependencies_exist: 'YES',
        earliest_start_date: '',
        created_by: 'Current User'
      });

      alert(`Integration group ${editingGroup ? 'updated' : 'created'} successfully!`);
    } catch (err: any) {
      console.error('Error saving group:', err);
      alert(`Failed to save integration group: ${err.message}`);
    }
  };

  const handleEditGroup = async (group: IntegrationGroup) => {
    setEditingGroup(group);

    // Properly format the date for the input field (YYYY-MM-DD format)
    const formattedDate = group.earliest_start_date
      ? group.earliest_start_date.split('T')[0]
      : '';

    setGroupForm({
      ...group,
      applications: group.applications || [],
      earliest_start_date: formattedDate
    });

    // Check if this group has any application dependencies
    try {
      const response = await authFetch(`/api/application-dependencies?integration_group_id=${group.group_id}`);
      const result = await response.json();
      if (result.success && result.data && result.data.length > 0) {
        setGroupHasDependencies(true);
      } else {
        setGroupHasDependencies(false);
      }
    } catch (err) {
      console.error('Error checking dependencies:', err);
      setGroupHasDependencies(false);
    }

    // Check if this group has any roadmap schedule records
    try {
      const scheduleResponse = await authFetch('/api/roadmap-schedule');
      const scheduleResult = await scheduleResponse.json();
      if (scheduleResult.success && Array.isArray(scheduleResult.data)) {
        const groupScheduleRecords = scheduleResult.data.filter(
          (record: any) => record.integration_group_id === group.group_id
        );
        setGroupHasScheduleRecords(groupScheduleRecords.length > 0);
      } else {
        setGroupHasScheduleRecords(false);
      }
    } catch (err) {
      console.error('Error checking schedule records:', err);
      setGroupHasScheduleRecords(false);
    }

    setShowCreateGroupModal(true);
  };

  const handleDeleteGroup = async (groupId: string) => {
    try {
      // First check if group has any roadmap schedule records
      const scheduleResponse = await authFetch('/api/roadmap-schedule');
      const scheduleResult = await scheduleResponse.json();

      if (scheduleResult.success && Array.isArray(scheduleResult.data)) {
        const groupScheduleRecords = scheduleResult.data.filter(
          (record: any) => record.integration_group_id === groupId
        );

        if (groupScheduleRecords.length > 0) {
          alert(`Cannot delete Integration Group: ${groupScheduleRecords.length} roadmap schedule record(s) exist for this group.`);
          return;
        }
      }

      // No schedule records, proceed with confirmation
      if (!window.confirm('Are you sure you want to delete this integration group?')) {
        return;
      }

      const response = await authFetch(`/api/integration-groups/${groupId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete group');
      }

      // Reload groups from database
      await loadIntegrationGroups();
      alert('Integration group deleted successfully!');
    } catch (err: any) {
      console.error('Error deleting group:', err);
      alert(`Failed to delete integration group: ${err.message}`);
    }
  };

  const handleAddAppsToGroup = (group: IntegrationGroup) => {
    setTargetGroup(group);
    setAppsToAdd([]);
    setShowAddAppsModal(true);
  };

  const handleAppToAddToggle = (appId: string) => {
    setAppsToAdd(prev => 
      prev.includes(appId) 
        ? prev.filter(id => id !== appId)
        : [...prev, appId]
    );
  };

  const handleSaveAdditionalApps = async () => {
    if (!targetGroup || appsToAdd.length === 0) {
      alert('Please select at least one application to add.');
      return;
    }

    try {
      // First validate the application assignments against the target group
      const validationResponse = await authFetch('/api/integration-groups/validate-assignments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          applications: appsToAdd,
          excludeGroupId: null,
          targetGroupId: targetGroup.group_id
        })
      });

      if (!validationResponse.ok) {
        throw new Error('Failed to validate application assignments');
      }

      const validationResult = await validationResponse.json();

      if (!validationResult.success || !validationResult.data.valid) {
        const restrictions = validationResult.data.restricted_applications || [];
        const errorMessages = restrictions.map((app: any) =>
          `${app.application_id}: ${app.conflicting_group.reason}`
        ).join('\n');

        alert(`Cannot add applications to this group:\n\n${errorMessages}`);
        return;
      }

      // Get current applications in the target group
      const currentApps = targetGroup.applications || [];
      // Combine with new applications
      const updatedApps = [...currentApps, ...appsToAdd];

      const response = await authFetch(`/api/integration-groups/${targetGroup.group_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...targetGroup, // Include all existing group properties
          applications: updatedApps,
          execution_sequence: updatedApps // Use same as applications for now
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add applications to group');
      }

      // Reload groups from database
      await loadIntegrationGroups();
      await loadRestrictedApplications(); // Reload restricted apps after group changes

      // Close modal and reset state
      setShowAddAppsModal(false);
      setTargetGroup(null);
      setAppsToAdd([]);

      alert(`Successfully added ${appsToAdd.length} application(s) to ${targetGroup.group_name}!`);
    } catch (err: any) {
      console.error('Error adding applications to group:', err);
      alert(`Failed to add applications: ${err.message}`);
    }
  };

  // Function to validate and remove application from group
  const handleRemoveApplication = async (groupId: string, applicationId: string) => {
    try {
      setRemovingApplications(prev => new Set(prev).add(applicationId));

      // Validate if application can be removed
      const validationResponse = await authFetch(`/api/integration-groups/validate-removal/${groupId}/${applicationId}`, {
        method: 'POST'
      });

      if (!validationResponse.ok) {
        throw new Error('Failed to validate application removal');
      }

      const validationResult = await validationResponse.json();

      if (!validationResult.success || !validationResult.data.canRemove) {
        alert(`Cannot remove application: ${validationResult.data.reason}`);
        return;
      }

      // If validation passes, remove from group form
      setGroupForm(prev => ({
        ...prev,
        applications: prev.applications?.filter(id => id !== applicationId) || []
      }));

      alert(`Application ${applicationId} removed from group successfully!`);
    } catch (error: any) {
      console.error('Error removing application:', error);
      alert(`Failed to remove application: ${error.message}`);
    } finally {
      setRemovingApplications(prev => {
        const newSet = new Set(prev);
        newSet.delete(applicationId);
        return newSet;
      });
    }
  };

  const getAvailableAppsForGroup = (group: IntegrationGroup) => {
    return applications.filter(app => {
      const appId = app.application_id_code;
      const isInCurrentGroup = group.applications?.includes(appId) || false;

      // Don't include apps already in the current group
      if (isInCurrentGroup) {
        return false;
      }

      // Check if app is restricted
      const isRestricted = restrictedApplications.includes(appId);

      if (isRestricted) {
        // If app is restricted, it cannot be added to any other group
        return false;
      }

      // For non-restricted apps, check normal assignment logic
      const assigned = getAssignedApplications();
      const isAssigned = assigned.includes(appId);

      // Allow if not assigned, or if editing and it's in current group
      return !isAssigned;
    });
  };

  // 4R Disposition Model Colors (from scoring thresholds database)
  const getRecommendationColor = (recommendation: string) => {
    switch (recommendation) {
      case 'Retire': return '#dc2626';
      case 'Re-Platform': return '#f59e0b';
      case 'Re-Design': return '#3b82f6';
      case 'Retain': return '#10b981';
      default: return '#6c757d';
    }
  };

  const getRecommendationIcon = (recommendation: string) => {
    switch (recommendation) {
      case 'Retire': return '🗑️';
      case 'Re-Platform': return '🔄';
      case 'Re-Design': return '🎨';
      case 'Retain': return '✅';
      default: return '❓';
    }
  };

  const getGroupCompositionLabel = (composition: string) => {
    switch (composition) {
      case 'retirement-only': return '🗑️ Retirement Only';
      case 'migration-only': return '🔄 Migration Only';
      case 'mixed': return '🔀 Mixed Group';
      default: return composition;
    }
  };

  const getTimingScenarioLabel = (timing: string) => {
    switch (timing) {
      case 'immediate-start': return '🚀 Immediate Start';
      case 'sequential-required': return '📋 Sequential Required';
      case 'parallel-possible': return '⚡ Parallel Possible';
      case 'external-timing': return '⏳ External Timing';
      default: return timing;
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="page-header">
          <h1 className="page-header__title">Loading...</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">Integration Groupings</h1>
            <p className="page-header__subtitle">
              Select and organize applications into integration groups for roadmap implementation planning
            </p>
          </div>
          <div className="page-header__actions">
            <button
              onClick={() => setActiveTab('selection')}
              style={{
                padding: '8px 16px',
                backgroundColor: activeTab === 'selection' ? '#3B82F6' : '#E5E7EB',
                color: activeTab === 'selection' ? 'white' : '#333',
                border: 'none',
                borderRadius: '4px 0 0 4px',
                cursor: 'pointer'
              }}
            >
              Application Selection
            </button>
            <button
              onClick={() => setActiveTab('groups')}
              style={{
                padding: '8px 16px',
                backgroundColor: activeTab === 'groups' ? '#3B82F6' : '#E5E7EB',
                color: activeTab === 'groups' ? 'white' : '#333',
                border: 'none',
                borderRadius: '0 4px 4px 0',
                cursor: 'pointer'
              }}
            >
              Integration Groups ({integrationGroups.length})
            </button>
          </div>
        </div>
      </div>

      <div className="master-data-container">
        {activeTab === 'selection' ? (
          <>
            {/* Search and Filter Bar */}
            <div className="md-card">
              <div className="md-card-content">
                <div className="md-filters-row">
                  <div className="md-search-field">
                    <div className="md-input-group">
                      <span className="md-input-icon">🔍</span>
                      <input
                        type="text"
                        placeholder="Search applications..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="md-input"
                      />
                    </div>
                  </div>
                  
                  <div className="md-filter-field">
                    <select
                      value={filterRecommendation}
                      onChange={(e) => setFilterRecommendation(e.target.value)}
                      className="md-select"
                    >
                      <option value="all">All Recommendations</option>
                      <option value="Retire">🗑️ Retire</option>
                      <option value="Re-Platform">🔄 Re-Platform</option>
                      <option value="Re-Design">🎨 Re-Design</option>
                      <option value="Retain">✅ Retain</option>
                    </select>
                  </div>

                  <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                    <button
                      onClick={handleSelectAll}
                      className="md-button--secondary"
                    >
                      Select All
                    </button>
                    <button
                      onClick={handleClearSelection}
                      className="md-button--secondary"
                    >
                      Clear Selection
                    </button>
                    <button
                      onClick={handleCreateGroup}
                      disabled={selectedApplications.length === 0}
                      className="md-button--primary"
                      style={{
                        opacity: selectedApplications.length === 0 ? 0.5 : 1,
                        cursor: selectedApplications.length === 0 ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Create Group ({selectedApplications.length} selected)
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Selected Applications Summary */}
            {selectedApplications.length > 0 && (
              <div className="md-card" style={{ marginBottom: '16px' }}>
                <div className="md-card-content">
                  <h3 style={{ marginBottom: '12px' }}>Selected Applications Analysis</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                    <div>
                      <strong>Group Composition:</strong> {getGroupCompositionLabel(analyzeGroupComposition(selectedApplications))}
                    </div>
                    <div>
                      <strong>Total Selected:</strong> {selectedApplications.length} applications
                    </div>
                    <div>
                      <strong>Retirements:</strong> {applications.filter(app => selectedApplications.includes(app.application_id_code) && app.recommendation === 'Retire').length}
                    </div>
                    <div>
                      <strong>Migrations:</strong> {applications.filter(app => selectedApplications.includes(app.application_id_code) && (app.recommendation === 'Re-Platform' || app.recommendation === 'Re-Design')).length}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Legend for Application Selection */}
            <div className="md-card" style={{ marginBottom: '16px', backgroundColor: '#F0FDF4', border: '1px solid #10B981' }}>
              <div className="md-card-content" style={{ padding: '12px' }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#059669' }}>📋 Application Selection Guide</h4>
                <div style={{ display: 'flex', gap: '24px', fontSize: '12px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '12px', height: '12px', backgroundColor: '#10B981', border: '2px solid #10B981' }}></div>
                    <span><strong>Retain / Re-Design</strong> - Can belong to multiple groups (start date constraint applies)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '12px', height: '12px', backgroundColor: '#EF4444', border: '1px solid #EF4444' }}></div>
                    <span><strong>Retire / Re-Platform</strong> - Can only belong to one group (disabled when assigned)</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Applications Table */}
            <div className="md-card">
              <div className="md-table-container">
                <table className="md-table">
                  <thead>
                    <tr>
                      <th className="md-table-header" style={{ width: '50px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={(() => {
                            const availableApps = getFilteredApplications().filter(app => !isApplicationAssigned(app.application_id_code));
                            return selectedApplications.length === availableApps.length && availableApps.length > 0;
                          })()}
                          onChange={() => {
                            const availableApps = getFilteredApplications().filter(app => !isApplicationAssigned(app.application_id_code));
                            if (selectedApplications.length === availableApps.length) {
                              handleClearSelection();
                            } else {
                              handleSelectAll();
                            }
                          }}
                        />
                      </th>
                      <th className="md-table-header" style={{ textAlign: 'left' }}>Application</th>
                      <th className="md-table-header" style={{ textAlign: 'center' }}>Technology</th>
                      <th className="md-table-header" style={{ textAlign: 'center' }}>Infrastructure</th>
                      <th className="md-table-header" style={{ textAlign: 'center' }}>Recommendation</th>
                      <th className="md-table-header" style={{ textAlign: 'center' }}>App Dependencies</th>
                      <th className="md-table-header" style={{ textAlign: 'center' }}>Assigned Group</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredApplications().map(app => {
                      const isAssigned = isApplicationAssigned(app.application_id_code);
                      const isSelected = selectedApplications.includes(app.application_id_code);
                      const isSingleGroupOnly = app.recommendation === 'Retire' || app.recommendation === 'Re-Platform';
                      const canMultipleGroups = app.recommendation === 'Retain' || app.recommendation === 'Re-Design';

                      return (
                        <tr
                          key={app.application_id_code}
                          className="md-table-row"
                          style={{
                            backgroundColor: isSelected ? '#EBF8FF' : 'transparent',
                            opacity: isAssigned && isSingleGroupOnly ? 0.5 : 1, // Dim RETIRE/RE-PLATFORM apps when assigned
                            color: isAssigned && isSingleGroupOnly ? '#999' : 'inherit',
                            border: canMultipleGroups ? '1px solid #10B981' : 'none', // Green border for multi-group apps
                            borderLeft: canMultipleGroups ? '4px solid #10B981' : 'none' // Thick green left border
                          }}
                        >
                          <td className="md-table-cell" style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={isAssigned && isSingleGroupOnly} // Disable RETIRE/RE-PLATFORM apps when assigned
                              onChange={() => handleApplicationToggle(app.application_id_code)}
                              style={{ cursor: (isAssigned && isSingleGroupOnly) ? 'not-allowed' : 'pointer' }}
                            />
                          </td>
                        <td className="md-table-cell">
                          <div className="md-app-info">
                            <div className="md-app-info__primary">{app.application_id_code}</div>
                            <div className="md-app-info__primary">{app.application_name}</div>
                          </div>
                        </td>
                        <td className="md-table-cell" style={{ textAlign: 'center' }}>
                          <span className="technology-info">
                            {app.technology_stack || 'Not specified'}
                          </span>
                        </td>
                        <td className="md-table-cell" style={{ textAlign: 'center' }}>
                          <span className="infrastructure-info">
                            {app.it_hardware_infrastructure || 'Not specified'}
                          </span>
                        </td>
                        <td className="md-table-cell" style={{ textAlign: 'center' }}>
                          <div 
                            className="md-chip"
                            style={{ 
                              backgroundColor: getRecommendationColor(app.recommendation),
                              color: 'white'
                            }}
                          >
                            {getRecommendationIcon(app.recommendation)} {app.recommendation}
                          </div>
                        </td>
                        <td className="md-table-cell" style={{ textAlign: 'center' }}>
                          {app.application_dependencies === 'YES' ? (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '6px 12px',
                              backgroundColor: '#1976d2',
                              color: 'white',
                              borderRadius: '16px',
                              fontSize: '14px',
                              fontWeight: '600',
                              boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)'
                            }}>
                              <span style={{ fontSize: '16px' }}>✓</span> YES
                            </span>
                          ) : (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '6px 12px',
                              backgroundColor: '#f5f5f5',
                              color: '#666',
                              borderRadius: '16px',
                              fontSize: '14px',
                              fontWeight: '500',
                              border: '1px solid #e0e0e0'
                            }}>
                              <span style={{ fontSize: '16px' }}>—</span> NO
                            </span>
                          )}
                        </td>
                        <td className="md-table-cell" style={{ textAlign: 'center' }}>
                          {(() => {
                            const assignedGroup = getAssignedGroup(app.application_id_code);
                            return assignedGroup ? (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                <strong style={{ fontSize: '12px', color: '#666' }}>{assignedGroup.group_id}</strong>
                                <div style={{ fontSize: '10px', color: '#999' }}>{assignedGroup.group_name}</div>
                              </div>
                            ) : (
                              <span style={{ fontSize: '12px', color: '#ccc' }}>-</span>
                            );
                          })()}
                        </td>
                      </tr>
                  );
                })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Integration Groups View */}
            <div className="md-card" style={{ marginBottom: '16px' }}>
              <div className="md-card-content">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                  <div>
                    <h3 style={{ margin: 0 }}>Integration Groups Overview</h3>
                    <p style={{ color: '#666', fontSize: '14px', margin: '4px 0 0' }}>
                      Manage and configure integration groups for roadmap implementation
                    </p>
                  </div>
                  <input
                    type="text"
                    placeholder="Search by application ID or name..."
                    value={groupSearchTerm}
                    onChange={(e) => setGroupSearchTerm(e.target.value)}
                    style={{
                      padding: '8px 12px', border: groupSearchTerm ? '2px solid #1976d2' : '1px solid #ddd',
                      borderRadius: '6px', fontSize: '14px', minWidth: '280px', outline: 'none'
                    }}
                  />
                </div>
                {groupSearchTerm && (
                  <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
                    {integrationGroups.filter(g => {
                      const s = groupSearchTerm.toLowerCase();
                      const appIds = (g.application_list || '').split(';').map((id: string) => id.trim());
                      const groupApps = applications.filter(a => appIds.includes(a.application_id_code));
                      return g.group_name.toLowerCase().includes(s) ||
                        g.group_id.toLowerCase().includes(s) ||
                        appIds.some((id: string) => id.toLowerCase().includes(s)) ||
                        groupApps.some(a => a.application_name.toLowerCase().includes(s));
                    }).length} of {integrationGroups.length} groups
                    <button onClick={() => setGroupSearchTerm('')} style={{ marginLeft: '8px', padding: '2px 8px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', backgroundColor: '#f5f5f5' }}>Clear</button>
                  </div>
                )}
              </div>
            </div>

            {integrationGroups.length === 0 ? (
              <div className="md-card">
                <div className="md-empty-state">
                  <div className="md-empty-icon">📦</div>
                  <h3>No Integration Groups</h3>
                  <p>Switch to Application Selection tab to create your first integration group</p>
                </div>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '16px' }}>
                {integrationGroups.filter(group => {
                  if (!groupSearchTerm.trim()) return true;
                  const s = groupSearchTerm.toLowerCase();
                  const appIds = (group.application_list || '').split(';').map((id: string) => id.trim());
                  const groupApps = applications.filter(a => appIds.includes(a.application_id_code));
                  return group.group_name.toLowerCase().includes(s) ||
                    group.group_id.toLowerCase().includes(s) ||
                    appIds.some((id: string) => id.toLowerCase().includes(s)) ||
                    groupApps.some(a => a.application_name.toLowerCase().includes(s));
                }).map(group => (
                  <div key={group.group_id} className="md-card">
                    <div className="md-card-content">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                        <div style={{ textAlign: 'left' }}>
                          <h3 style={{ margin: '0 0 8px 0', textAlign: 'left' }}>
                            {group.group_id} - {group.group_name}
                          </h3>
                          <p style={{ color: '#666', fontSize: '14px', margin: 0, textAlign: 'left' }}>{group.group_description}</p>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleAddAppsToGroup(group)}
                            className="md-button--secondary"
                            style={{ padding: '6px 12px', fontSize: '14px' }}
                          >
                            Add Apps
                          </button>
                          <button
                            onClick={() => handleEditGroup(group)}
                            className="md-button--secondary"
                            style={{ padding: '6px 12px', fontSize: '14px' }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteGroup(group.group_id)}
                            className="md-button--secondary"
                            style={{ padding: '6px 12px', fontSize: '14px' }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      {/* Single line summary */}
                      <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '4px', fontSize: '16px' }}>
                        <strong style={{ fontSize: '16px', color: '#666' }}>Applications:</strong> {group.applications?.length || 0} apps | {' '}
                        <strong style={{ fontSize: '16px', color: '#666' }}>Start Date:</strong> {group.earliest_start_date ? group.earliest_start_date.split('T')[0] : 'TBD'} | {' '}
                        <strong style={{ fontSize: '16px', color: '#666' }}>Created:</strong> {group.created_date ? group.created_date.split('T')[0] : 'Unknown'} | {' '}
                        <strong style={{ fontSize: '16px', color: '#666' }}>App Dependencies:</strong>
                        <span className={`md-chip ${group.application_dependencies_exist === 'YES' ? 'chip-warning' : 'chip-success'}`} style={{margin: '0 4px', fontSize: '14px'}}>
                          {group.application_dependencies_exist || 'YES'}
                        </span>
                        {group.exclude_from_analysis && (
                          <>
                            {' | '}
                            <span style={{ fontSize: '12px', padding: '3px 10px', backgroundColor: '#fee2e2', color: '#dc2626', borderRadius: '12px', fontWeight: 500, marginLeft: '4px' }}>
                              Excl. Cost & Benefit
                            </span>
                          </>
                        )}
                      </div>

                      <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '12px' }}>
                        <strong style={{ fontSize: '16px', color: '#666' }}>Group Applications:</strong>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '8px' }}>
                          {group.applications?.map(appId => {
                            const app = applications.find(a => a.application_id_code === appId);
                            if (!app) return null;
                            return (
                              <div
                                key={appId}
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  padding: '8px 12px',
                                  backgroundColor: '#f9fafb',
                                  borderRadius: '6px',
                                  border: '1px solid #e5e7eb',
                                  minWidth: '140px'
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                  <span style={{ fontSize: '14px', fontWeight: '600', color: getRecommendationColor(app.recommendation) }}>{appId}</span>
                                  <div
                                    className="md-chip"
                                    style={{
                                      backgroundColor: getRecommendationColor(app.recommendation),
                                      color: 'white',
                                      fontSize: '12px',
                                      padding: '2px 6px'
                                    }}
                                  >
                                    {getRecommendationIcon(app.recommendation)} {app.recommendation}
                                  </div>
                                </div>
                                <div style={{ fontSize: '13px', fontWeight: 500, color: '#111827', textAlign: 'center', maxWidth: '160px' }}>
                                  {app.application_name}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Create/Edit Group Modal */}
      {showCreateGroupModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ marginBottom: '24px', fontSize: '20px' }}>
              {editingGroup ? 'Edit Integration Group' : 'Create Integration Group'}
            </h2>

            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '16px' }}>Group Name *</label>
                <input
                  type="text"
                  value={groupForm.group_name}
                  onChange={(e) => setGroupForm({ ...groupForm, group_name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '4px',
                    fontSize: '16px'
                  }}
                  placeholder="e.g., Phase 1 - Legacy Retirement"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '16px' }}>Description</label>
                <textarea
                  value={groupForm.group_description}
                  onChange={(e) => setGroupForm({ ...groupForm, group_description: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '4px',
                    minHeight: '80px',
                    fontSize: '16px'
                  }}
                  placeholder="Describe the purpose and scope of this integration group..."
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '16px' }}>Earliest Start Date</label>
                  {(() => {
                    const minStartDate = getMinimumStartDateForApplications(groupForm.applications || []);
                    const appsWithConstraints = getAppsWithStartDateConstraints(groupForm.applications || []);
                    const isDateLocked = editingGroup && groupHasScheduleRecords;
                    return (
                      <>
                        <input
                          type="date"
                          value={groupForm.earliest_start_date || ''}
                          min={minStartDate || undefined}
                          onChange={(e) => setGroupForm({ ...groupForm, earliest_start_date: e.target.value })}
                          disabled={!!isDateLocked}
                          style={{
                            width: '100%',
                            padding: '12px',
                            border: isDateLocked ? '1px solid #D1D5DB' : (minStartDate ? '2px solid #F59E0B' : '1px solid #D1D5DB'),
                            borderRadius: '4px',
                            fontSize: '16px',
                            backgroundColor: isDateLocked ? '#f5f5f5' : (minStartDate ? '#FFFBEB' : 'white'),
                            color: isDateLocked ? '#9e9e9e' : 'inherit',
                            cursor: isDateLocked ? 'not-allowed' : 'pointer'
                          }}
                        />
                        {isDateLocked && (
                          <small style={{ color: '#DC2626', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                            Cannot be changed - roadmap schedule records exist for this group
                          </small>
                        )}
                        {appsWithConstraints.length > 0 && (
                          <div style={{
                            marginTop: '8px',
                            padding: '8px 12px',
                            backgroundColor: '#FEF3C7',
                            border: '1px solid #F59E0B',
                            borderRadius: '4px',
                            fontSize: '12px',
                            color: '#92400E'
                          }}>
                            <strong>⚠️ Start Date Constraint:</strong>
                            <div style={{ marginTop: '4px' }}>
                              {appsWithConstraints.map((c, idx) => (
                                <div key={idx}>
                                  <strong>{c.appId}</strong> must complete deployment in "{c.groupName}" by {c.deploymentEndDate}
                                </div>
                              ))}
                            </div>
                            <div style={{ marginTop: '4px', fontWeight: 'bold' }}>
                              Minimum start date: {minStartDate}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '16px' }}>Application Dependencies Exist</label>
                  <select
                    value={groupForm.application_dependencies_exist}
                    onChange={(e) => setGroupForm({ ...groupForm, application_dependencies_exist: e.target.value as 'YES' | 'NO' })}
                    className="md-select"
                    style={{
                      fontSize: '16px',
                      padding: '12px',
                      backgroundColor: (editingGroup && groupHasDependencies) ? '#f5f5f5' : 'white',
                      color: (editingGroup && groupHasDependencies) ? '#9e9e9e' : 'inherit',
                      cursor: (editingGroup && groupHasDependencies) ? 'not-allowed' : 'pointer'
                    }}
                    disabled={!!(editingGroup && groupHasDependencies)}
                    title={editingGroup && groupHasDependencies ? "Cannot be changed when application dependencies exist for this group" : ""}
                  >
                    <option value="YES">YES</option>
                    <option value="NO">NO</option>
                  </select>
                  {editingGroup && groupHasDependencies && (
                    <small style={{ color: '#DC2626', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      Cannot be changed - application dependencies exist for this group
                    </small>
                  )}
                </div>
              </div>

              {/* Exclusion Flag */}
              <div style={{ padding: '12px', backgroundColor: '#fef9e7', borderRadius: '8px', border: '1px solid #f0e68c' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                  <input
                    type="checkbox"
                    checked={groupForm.exclude_from_analysis || false}
                    onChange={(e) => setGroupForm({ ...groupForm, exclude_from_analysis: e.target.checked })}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  Not relevant for Disposition Cost & Benefit Realization
                </label>
              </div>

              {/* License Renegotiation Flag */}
              <div style={{ padding: '12px', backgroundColor: '#fff3e0', borderRadius: '8px', border: '1px solid #ffcc80' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: '#e65100', fontWeight: '500' }}>
                  <input
                    type="checkbox"
                    checked={groupForm.license_renegotiation || false}
                    onChange={(e) => setGroupForm({ ...groupForm, license_renegotiation: e.target.checked })}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  License Renegotiation Group
                </label>
                <p style={{ margin: '4px 0 0 26px', fontSize: '12px', color: '#666' }}>
                  This group contains applications eligible for license cost renegotiation
                </p>
              </div>

              {/* Consolidation Group Flag */}
              <div style={{ padding: '12px', backgroundColor: '#f3e8ff', borderRadius: '8px', border: '1px solid #d8b4fe' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: '#7c3aed', fontWeight: '500' }}>
                  <input
                    type="checkbox"
                    checked={groupForm.consolidation_group || false}
                    onChange={(e) => setGroupForm({ ...groupForm, consolidation_group: e.target.checked })}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  Consolidation Group
                </label>
                <p style={{ margin: '4px 0 0 26px', fontSize: '12px', color: '#666' }}>
                  This group contains applications being consolidated into other applications
                </p>
              </div>

              {/* Cloud Migration Flag */}
              <div style={{ padding: '12px', backgroundColor: '#e0f2fe', borderRadius: '8px', border: '1px solid #7dd3fc' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: '#0369a1', fontWeight: '500' }}>
                  <input
                    type="checkbox"
                    checked={groupForm.cloud_migration || false}
                    onChange={(e) => setGroupForm({ ...groupForm, cloud_migration: e.target.checked })}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  Cloud Migration Group
                </label>
                <p style={{ margin: '4px 0 0 26px', fontSize: '12px', color: '#666' }}>
                  This group contains applications migrating from on-premise to cloud
                </p>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '16px' }}>
                  Selected Applications ({groupForm.applications?.length || 0})
                </label>
                <div style={{
                  border: '1px solid #D1D5DB',
                  borderRadius: '4px',
                  padding: '8px',
                  maxHeight: '150px',
                  overflowY: 'auto'
                }}>
                  {groupForm.applications?.map(appId => {
                    const app = applications.find(a => a.application_id_code === appId);
                    const isRemoving = removingApplications.has(appId);
                    return (
                      <div
                        key={appId}
                        style={{
                          fontSize: '16px',
                          margin: '4px',
                          color: '#333',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '6px 12px',
                          border: '1px solid #E5E7EB',
                          borderRadius: '6px',
                          backgroundColor: '#F9FAFB'
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {appId}
                          {app && (
                            <div
                              className="md-chip"
                              style={{
                                backgroundColor: getRecommendationColor(app.recommendation),
                                color: 'white',
                                fontSize: '12px',
                                padding: '3px 6px'
                              }}
                            >
                              {getRecommendationIcon(app.recommendation)} {app.recommendation}
                            </div>
                          )}
                        </span>
                        {editingGroup && (
                          <button
                            onClick={() => handleRemoveApplication(editingGroup.group_id, appId)}
                            disabled={isRemoving}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#DC2626',
                              cursor: isRemoving ? 'not-allowed' : 'pointer',
                              fontSize: '16px',
                              padding: '2px',
                              display: 'flex',
                              alignItems: 'center',
                              opacity: isRemoving ? 0.5 : 1
                            }}
                            title={isRemoving ? 'Removing...' : 'Remove application from group'}
                          >
                            {isRemoving ? '⏳' : '❌'}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={() => {
                  setShowCreateGroupModal(false);
                  setEditingGroup(null);
                  setGroupHasDependencies(false);
                  setGroupHasScheduleRecords(false);
                }}
                className="md-button--secondary"
                style={{ fontSize: '16px', padding: '12px 24px' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveGroup}
                className="md-button--primary"
                style={{ fontSize: '16px', padding: '12px 24px' }}
                disabled={!groupForm.group_name}
              >
                {editingGroup ? 'Update Group' : 'Create Group'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Applications Modal */}
      {showAddAppsModal && targetGroup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            minWidth: '600px',
            maxWidth: '800px',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ margin: '0 0 16px 0' }}>Add Applications to {targetGroup.group_name}</h2>
            
            <p style={{ color: '#666', marginBottom: '20px', fontSize: '14px' }}>
              Select applications to add to this integration group.
            </p>

            {getAvailableAppsForGroup(targetGroup).length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
                <h3>No Available Applications</h3>
                <p>No applications available. All applications are either already assigned to groups or already in this group.</p>
              </div>
            ) : (
              <div>
                <div style={{ marginBottom: '16px' }}>
                  <strong>Available Applications ({getAvailableAppsForGroup(targetGroup).length}):</strong>
                </div>
                
                <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: '4px' }}>
                  {getAvailableAppsForGroup(targetGroup).map(app => (
                    <div
                      key={app.application_id_code}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px 16px',
                        borderBottom: '1px solid #F3F4F6',
                        backgroundColor: appsToAdd.includes(app.application_id_code) ? '#EBF8FF' : 'white'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={appsToAdd.includes(app.application_id_code)}
                        onChange={() => handleAppToAddToggle(app.application_id_code)}
                        style={{ marginRight: '12px' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{app.application_id_code}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{app.application_name}</div>
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        <div 
                          className="md-chip"
                          style={{ 
                            backgroundColor: getRecommendationColor(app.recommendation),
                            color: 'white',
                            fontSize: '10px',
                            padding: '2px 6px'
                          }}
                        >
                          {getRecommendationIcon(app.recommendation)} {app.recommendation}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {appsToAdd.length > 0 && (
                  <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#F3F4F6', borderRadius: '4px' }}>
                    <strong>Selected: {appsToAdd.length} application(s)</strong>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      {appsToAdd.join(', ')}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={() => {
                  setShowAddAppsModal(false);
                  setTargetGroup(null);
                  setAppsToAdd([]);
                }}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #D1D5DB',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAdditionalApps}
                disabled={appsToAdd.length === 0}
                className="md-button--primary"
                style={{
                  opacity: appsToAdd.length === 0 ? 0.5 : 1,
                  cursor: appsToAdd.length === 0 ? 'not-allowed' : 'pointer'
                }}
              >
                Add {appsToAdd.length} Application(s)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Existing Relationships Warning Modal */}
      {showExistingRelationshipsWarning && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          backgroundColor: 'rgba(0, 0, 0, 0.5)', 
          zIndex: 1000, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          <div style={{ 
            backgroundColor: 'white', 
            borderRadius: '8px', 
            padding: '24px', 
            maxWidth: '600px', 
            width: '90%', 
            maxHeight: '80vh', 
            overflowY: 'auto', 
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            position: 'relative'
          }}>
            <div style={{ marginBottom: '20px' }}>
              <h2 style={{ color: '#DC2626', margin: 0, fontSize: '20px' }}>⚠️ Warning: Existing Group Relationships</h2>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <p>Selected applications: {selectedApplications.join(', ')}</p>
              <p>Found {existingRelationships.length} existing relationships</p>
              
              {existingRelationships.length > 0 ? (
                <div>
                  <h4>Existing Relationships:</h4>
                  {existingRelationships.map((rel, index) => (
                    <div key={index} style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                      <strong>{rel.appId} - {rel.appName}</strong>
                      <div>Assigned to groups:</div>
                      {rel.groups.map((group, gIndex) => (
                        <div key={gIndex} style={{ marginLeft: '10px' }}>
                          • {group.groupId} - {group.groupName}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ) : (
                <p>No existing relationships found, but modal was triggered.</p>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowExistingRelationshipsWarning(false);
                  setExistingRelationships([]);
                }}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #ccc',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowExistingRelationshipsWarning(false);
                  setExistingRelationships([]);
                  proceedWithGroupCreation();
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Continue Creating Group
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationGroupings;